/**
 * Transaction Hooks
 * TanStack Query hooks for transaction data
 */

import { useQuery, useMutation } from '@tanstack/react-query'
import {
    getTransactions,
    getTransactionById,
    downloadTransactionReceipt,
    downloadMultipleTransactionReceipts,
    downloadUserTransactionHistory,
    downloadUsersTransactionHistory,
    type DownloadUserHistoryParams,
    type DownloadUsersHistoryParams,
} from '@/services/transactions.service'
import type {
    TransactionListResponse,
    TransactionDetailResponse,
    TransactionFilters,
} from '@/types/transaction'

// Legacy type for backward compatibility
export interface Transaction {
    id: string
    date: string
    accountName: string
    walletAddress: string
    txnType: 'Buy' | 'Sell' | 'Convert' | 'Receive' | 'Send'
    amount: number
    token: 'BTC' | 'USDT' | 'USDC' | 'ETH' | 'DOGE'
    status: 'Completed' | 'Pending' | 'Failed' | 'Cancelled'
}

/**
 * Hook to fetch transactions list with filters
 * GET /admin/transactions
 * @param filters - Optional query parameters for filtering transactions
 */
export const useTransactions = (filters?: TransactionFilters) => {
    return useQuery<TransactionListResponse>({
        queryKey: ['transactions', filters],
        queryFn: () => getTransactions(filters),
        staleTime: 1000 * 30, // 30 seconds
    })
}

/**
 * Hook for fetching a single transaction by ID
 * GET /api/v1/admin/transactions/{id}
 * @param id - Transaction ID
 */
export const useTransaction = (id: string) => {
    return useQuery<TransactionDetailResponse>({
        queryKey: ['transaction', id],
        queryFn: () => getTransactionById(id),
        enabled: !!id,
        staleTime: 1000 * 60, // 1 minute
    })
}

/**
 * Hook for downloading a single transaction receipt
 * GET /admin/transactions/download/receipt/{id}
 */
export const useDownloadReceipt = () => {
    return useMutation({
        mutationFn: (id: string) => downloadTransactionReceipt(id),
    })
}

/**
 * Hook for downloading multiple transaction receipts
 * POST /admin/transactions/download/receipts
 */
export const useDownloadMultipleReceipts = () => {
    return useMutation({
        mutationFn: (transactionIds: string[]) =>
            downloadMultipleTransactionReceipts(transactionIds),
    })
}

/**
 * Hook for downloading transaction history PDF for a single user
 * POST /admin/transactions/download/user-history
 */
export const useDownloadUserTransactionHistory = () => {
    return useMutation({
        mutationFn: (params: DownloadUserHistoryParams) =>
            downloadUserTransactionHistory(params),
    })
}

/**
 * Hook for downloading transaction history PDF for multiple users
 * POST /admin/transactions/download/users-history
 */
export const useDownloadUsersTransactionHistory = () => {
    return useMutation({
        mutationFn: (params: DownloadUsersHistoryParams) =>
            downloadUsersTransactionHistory(params),
    })
}
